import React from 'react';
import { Link } from 'react-router-dom';
import {
  FileText,
  Search,
  Zap,
  Star,
  Play,
  Book,
  RefreshCw,
  Trophy,
  BarChart2,
  MessageCircle,
  TrendingUp
} from 'lucide-react';

const AboutPage: React.FC = () => {
  return (
    <div className="about-page-container fade-in">
      <div className="about-content">
        <header className="about-header">
          <h1 className="about-title">About KNOW</h1>
          <div className="about-subtitle">
            Test your knowledge, strengthen your faith
          </div>
        </header>
        
        <section className="about-section about-section-elevated">
          <div className="about-section-header">
            <div className="about-section-icon-wrapper">
              <FileText size={32} color="#3b82f6" />
            </div>
            <h2>Our Mission</h2>
          </div>
          <div className="about-section-content">
            <p>
              KNOW is a Bible trivia game designed to help you test and expand your knowledge
              of Scripture in a fun and engaging way. We believe that learning about the Bible
              should be exciting and accessible to everyone, from beginners to scholars.
            </p>
          </div>
        </section>
        
        {/* Emraay Games Section */}
        <section className="about-section emraay-section about-section-elevated">
          <div className="about-section-header">
            <div className="emraay-logo-wrapper">
              <div className="emraay-logo">
                <img
                  src="https://res.cloudinary.com/dh5ugs2aa/image/upload/v1746760117/ajtwg9bdn83ilhsgwtft.jpg"
                  alt="Emraay Games Logo"
                />
              </div>
            </div>
            <h2>Developed By Emraay Games</h2>
          </div>

          <div className="emraay-content">
            <div className="emraay-description">
              <p>
                KNOW is proudly developed by Emraay Games, a division of Emraay Solutions -
                an IT services company with over 10 years of experience in building innovative
                digital experiences. Our team of passionate developers combines technical expertise
                with a deep appreciation for Scripture to create engaging educational games.
              </p>
            </div>

            <div className="emraay-values">
              <div className="emraay-value-card">
                <div className="emraay-value-icon">
                  <Search size={28} color="#3b82f6" />
                </div>
                <h3>Biblical Accuracy</h3>
                <p>We are committed to ensuring all content is scripturally accurate and respectful.</p>
              </div>

              <div className="emraay-value-card">
                <div className="emraay-value-icon">
                  <Zap size={28} color="#3b82f6" />
                </div>
                <h3>Educational Focus</h3>
                <p>Our games are designed to make learning Scripture engaging and accessible.</p>
              </div>

              <div className="emraay-value-card">
                <div className="emraay-value-icon">
                  <Star size={28} color="#3b82f6" />
                </div>
                <h3>Technical Excellence</h3>
                <p>We leverage cutting-edge technology to create smooth, intuitive experiences.</p>
              </div>
            </div>

            <div className="emraay-connect">
              <p>
                With a global presence serving clients in over 15 countries, Emraay brings
                world-class software engineering expertise to every project. Learn more about
                our parent company at <a href="https://emraaysolutions.com/" target="_blank" rel="noopener noreferrer">emraaysolutions.com</a>.
              </p>
            </div>
          </div>
        </section>
        
        <section className="about-section about-section-elevated">
          <div className="about-section-header">
            <div className="about-section-icon-wrapper">
              <Play size={32} color="#3b82f6" />
            </div>
            <h2>Game Features</h2>
          </div>
          <div className="about-features-grid">
            <div className="about-feature-card">
              <div className="about-feature-icon">
                <Book size={28} color="#3b82f6" />
              </div>
              <h3>Multiple Categories</h3>
              <p>Questions from both Old and New Testament, covering major stories, characters, and teachings</p>
            </div>
            <div className="about-feature-card">
              <div className="about-feature-icon">
                <RefreshCw size={28} color="#3b82f6" />
              </div>
              <h3>Regular Updates</h3>
              <p>New questions added regularly to keep the challenge fresh and engaging</p>
            </div>
            <div className="about-feature-card">
              <div className="about-feature-icon">
                <Trophy size={28} color="#3b82f6" />
              </div>
              <h3>Track Progress</h3>
              <p>See your scores improve as you learn more about the Bible</p>
            </div>
            <div className="about-feature-card">
              <div className="about-feature-icon">
                <BarChart2 size={28} color="#3b82f6" />
              </div>
              <h3>Leaderboards</h3>
              <p>Compete with friends and other players around the world</p>
            </div>
          </div>
        </section>
        
        <section className="about-section about-section-elevated">
          <div className="about-section-header">
            <div className="about-section-icon-wrapper">
              <MessageCircle size={32} color="#3b82f6" />
            </div>
            <h2>Did You Know?</h2>
          </div>
          <div className="about-fact-box">
            <div className="about-fact-content">
              <p className="about-fact">
                The Bible is the best-selling book of all time with over 5 billion copies sold worldwide,
                and has been translated into more than 3,000 languages.
              </p>
            </div>
          </div>
        </section>
        
        <section className="about-section about-section-elevated">
          <div className="about-section-header">
            <div className="about-section-icon-wrapper">
              <TrendingUp size={32} color="#3b82f6" />
            </div>
            <h2>Ready to Play?</h2>
          </div>
          <div className="about-section-content">
            <p className="about-cta-text">
              Whether you're a Bible scholar or just beginning your journey with Scripture,
              KNOW has questions for every knowledge level. Challenge yourself today!
            </p>

            <div className="about-actions">
              <Link to="/" className="about-btn about-btn-primary">Start Playing</Link>
              <Link to="/leaderboard" className="about-btn about-btn-secondary">View Leaderboard</Link>
            </div>
          </div>
        </section>
        
        <footer className="about-footer">
          <p>KNOW Bible Trivia &copy; {new Date().getFullYear()}</p>
          <p className="about-bible-verse">"Your word is a lamp for my feet, a light on my path." — Psalm 119:105</p>
        </footer>
      </div>
    </div>
  );
};

export default AboutPage;